/** Key on content_student_comment.customData for ELA reading fluency WPM. */
export const READING_FLUENCY_CUSTOM_DATA_KEY = "readingFluency";

export const getReadingFluencyFromCustomData = (customData) => {
    let data = customData;
    if (typeof data === "string") {
        try {
            data = JSON.parse(data);
        } catch {
            return "";
        }
    }
    if (!data || typeof data !== "object") return "";
    const raw = data[READING_FLUENCY_CUSTOM_DATA_KEY];
    if (raw == null) return "";
    return String(raw).trim();
};

/**
 * Merge readingFluency into existing customData for PUT.
 * Empty WPM omits the key; empty object → undefined (omit customData on wire).
 */
export const mergeReadingFluencyCustomData = (existingCustomData, readingFluency) => {
    const next = { ...(existingCustomData && typeof existingCustomData === "object" ? existingCustomData : {}) };
    const wpm = String(readingFluency ?? "").trim();
    if (wpm) next[READING_FLUENCY_CUSTOM_DATA_KEY] = wpm;
    else delete next[READING_FLUENCY_CUSTOM_DATA_KEY];
    return Object.keys(next).length ? next : undefined;
};

/**
 * Overlay in-memory / saved readingFluency onto gradingCriteriaDtos for parent preview.
 * Teacher preview EPR payloads often lack customData until the guardian DTO catches up.
 * Empty WPM for a known criteria id clears readingFluency on that DTO (so clear is visible
 * in preview without waiting for save+refetch).
 *
 * @param {Object} epr - guardian-shaped EPR (studentProfileData)
 * @param {Object<string, string>} fluencyByCriteriaId - map of gradingCriteriaId → WPM
 * @param {Object<string, string>} [fluencyBySubjectName] - fallback map of subjectName → WPM
 *   (covers preview when criteria ids don't match or ELA has fluency but no content ratings)
 * @returns {Object} same epr or shallow clone with enriched criteria
 */
export const enrichEprGradingCriteriaWithFluency = (epr, fluencyByCriteriaId = {}, fluencyBySubjectName = {}) => {
    if (!epr) return epr;

    const byId = fluencyByCriteriaId && typeof fluencyByCriteriaId === "object" ? fluencyByCriteriaId : {};
    const bySubject = fluencyBySubjectName && typeof fluencyBySubjectName === "object" ? fluencyBySubjectName : {};
    const hasById = Object.keys(byId).length > 0;
    const hasBySubject = Object.keys(bySubject).length > 0;
    if (!hasById && !hasBySubject) return epr;

    let changed = false;
    const existing = Array.isArray(epr.gradingCriteriaDtos) ? epr.gradingCriteriaDtos : [];
    const gradingCriteriaDtos = existing.map((dto) => {
        const id = String(dto.key ?? dto.gradingCriteriaId ?? "");
        let wpm;
        if (hasById && Object.prototype.hasOwnProperty.call(byId, id)) {
            wpm = byId[id];
        } else if (
            hasBySubject &&
            dto.subjectName &&
            Object.prototype.hasOwnProperty.call(bySubject, dto.subjectName)
        ) {
            wpm = bySubject[dto.subjectName];
        } else {
            return dto;
        }

        const nextCustomData = mergeReadingFluencyCustomData(dto.customData, wpm) ?? null;
        const prevCustomData =
            dto.customData && typeof dto.customData === "object" && Object.keys(dto.customData).length
                ? dto.customData
                : null;

        if (JSON.stringify(prevCustomData) === JSON.stringify(nextCustomData)) return dto;
        changed = true;
        return { ...dto, customData: nextCustomData };
    });

    // Fluency-only subjects may be missing from preview DTOs when there are no content ratings.
    if (hasBySubject) {
        Object.entries(bySubject).forEach(([subjectName, wpm]) => {
            const trimmed = String(wpm ?? "").trim();
            if (!trimmed) return;
            if (gradingCriteriaDtos.some((dto) => dto.subjectName === subjectName)) return;
            gradingCriteriaDtos.push({
                subjectName,
                ordinal: gradingCriteriaDtos.length,
                customData: { [READING_FLUENCY_CUSTOM_DATA_KEY]: trimmed },
                cdeStandards: []
            });
            changed = true;
        });
    }

    return changed ? { ...epr, gradingCriteriaDtos } : epr;
};
